import React, { useState, useEffect } from 'react';
import { ModalBody, PopoverBody } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import useApi from 'hooks/useApi';
import { loadAllStates, loadAllStatesTranslate, paginateStates } from 'redux/actions/stateActions';

import { TextField } from 'shared/components/form/Inputs';
import { colors } from 'shared/defaultTheme';
import { ButtonLabelIcon, Button } from 'shared/components/Buttons';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { LabelIcon } from 'shared/components/Labels';
import { ReactComponent as SortByIcon } from 'assets/icon-sort-by.svg';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icon-arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from 'assets/icon-arrow-up.svg';
import { ReactComponent as TranslateIcon } from 'assets/icon-translate.svg';

import TranslateFormModal from './TranslateFormModal';
import StatesTable from './StatesTable';
import StateForm from './StateForm';
import { StateFormModal, Popover, OptionMenu, StatesWrapper } from './styles';

const States = ({ urlFragment }) => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [currentState, setCurrentState] = useState(null);
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState(false);

  const PAGE_SIZE = 10;

  const [{ loading }, getStatesData] = customAreaApi(
    {
      url: `${window.API_V1}/${urlFragment}/${auth.cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getStatesTranslateData] = customAreaApi(
    {
      url: `${window.API_V1}/${urlFragment}/${auth.cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );

  const getSortIcon = () => {
    if (sort) {
      return <ArrowUpIcon fill={colors.black} />;
    }
    if (!sort) {
      return <ArrowDownIcon fill={colors.black} />;
    }
    return null;
  };

  const onSort = () => {
    setSort(!sort);
  };

  useEffect(async () => {
    const searchParam = {
      filters: [
        {
          model: 'StateClient',
          field: 'Name',
          asc: true,
        },
        {
          model: 'I18List',
          field: 'I18Code',
        },
      ],
      reduce: {
        limit: 0,
        skip: 0,
      },
    };

    const {
      data: { data },
    } = await getStatesData({
      data: searchParam,
    });

    const sortedData = [...data];

    sortedData.sort((a, b) => {
      const nameA = a.i18List
        .find((item) => item.i18Code === selectedDefaultLanguage)
        ?.i18Name.toUpperCase();
      const nameB = b.i18List
        .find((item) => item.i18Code === selectedDefaultLanguage)
        ?.i18Name.toUpperCase();

      if (nameA < nameB) {
        return sort ? -1 : 1;
      }
      if (nameA > nameB) {
        return sort ? 1 : -1;
      }
      return 0;
    });

    dispatch(loadAllStates(sortedData));
    dispatch(loadAllStatesTranslate(sortedData));
  }, [sort, selectedDefaultLanguage]);

  const refreshStatesData = async () => {
    setSort(false);
    try {
      const searchParam = {
        filters: [
          {
            model: 'StateClient',
            field: 'Name',
            asc: true,
          },
          {
            model: 'I18List',
            field: 'I18Code',
            filter: [selectedDefaultLanguage],
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      };
      const {
        data: { data },
      } = await getStatesData({
        data: searchParam,
      });
      dispatch(loadAllStates(data.filter((x) => x.id)));
    } catch (error) {
      dispatch(loadAllStates([]));
    }
  };

  const refreshStateTranslateData = async () => {
    setSort(false);
    try {
      const searchParam = {
        filters: [
          {
            model: 'StateClient',
            field: 'Name',
            asc: true,
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      };
      const {
        data: { data },
      } = await getStatesTranslateData({
        data: searchParam,
      });
      dispatch(loadAllStates(data.filter((x) => x.id)));
      dispatch(loadAllStatesTranslate(data.filter((x) => x.id)));
    } catch (error) {
      dispatch(loadAllStates([]));
      dispatch(loadAllStatesTranslate([]));
    }
  };

  useEffect(() => {
    refreshStatesData();
    refreshStateTranslateData();
  }, [selectedDefaultLanguage]);

  useEffect(() => {
    return () => {
      dispatch(loadAllStates([]));
      dispatch(loadAllStatesTranslate([]));
      dispatch(paginateStates({ page: 1, pageSize: PAGE_SIZE }));
    };
  }, []);

  const searchStates = async (e) => {
    if (e) {
      const { value } = e.target;
      setSearchValue(value);
      try {
        const {
          data: { data },
        } = await getStatesData({
          data: {
            filters: [
              {
                model: 'I18List',
                field: 'Name',
                isRegex: true,
              },
              {
                model: 'I18List',
                field: 'I18Code',
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          },
        });
        dispatch(
          loadAllStates(
            data.filter((item) => {
              return item.i18List.some((i18Item) =>
                i18Item.i18Name.toLowerCase().includes(value.toLowerCase())
              );
            })
          )
        );
      } catch (error) {
        dispatch(loadAllStates([]));
      }
    } else {
      setSearchValue('');
      try {
        const {
          data: { data },
        } = await getStatesData({
          data: {
            filters: [
              {
                model: 'I18List',
                field: 'Name',
                filter: [''],
                isRegex: true,
              },
              {
                model: 'I18List',
                field: 'I18Code',
                filter: [selectedDefaultLanguage],
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          },
        });
        dispatch(loadAllStates(data));
      } catch (error) {
        dispatch(loadAllStates([]));
      }
    }
  };

  const handleRefresh = () => {
    refreshStatesData();
    refreshStateTranslateData();
  };

  const handleToggleOption = () => {
    setOpenFormModal(true);
  };

  const onCloseFormModal = () => {
    setOpenFormModal(false);
    setCurrentState(null);
  };

  const onTranslate = () => {
    setShowTranslateModal(!showTranslateModal);
  };

  return (
    <StatesWrapper className="my-3">
      <div className="my-3">
        <Button
          className="translate-button"
          type="button"
          color="secondary"
          label={t('general_translate')}
          icon={<TranslateIcon />}
          onClick={onTranslate}
        />
      </div>
      <div className="d-flex justify-content-end action-header-mobile">
        <div className="form w-25 h-50 mr-4 w-container-search-mobile">
          <TextField
            Icon={() => null}
            borderRadius={2}
            className="mb-4 w-search m-0 h-50"
            inputClassName="w-100"
            buttonClassName=""
            withButtonClear
            rightIcon={<SearchIcon fill={colors.black} />}
            onChange={searchStates}
            value={searchValue ?? ''}
          />
        </div>
        <div className="d-flex btn-header-action">
          <ButtonLabelIcon
            type="button"
            Icon={() => <SortByIcon fill={colors.black} />}
            className="button-icon mr-1"
            text={t('general_order_by')}
            id="PopoverFocus"
          />
          <ButtonLabelIcon
            id="refresh-icon"
            text={t('general_button_refresh')}
            type="button"
            Icon={() => <RefreshIcon fill={colors.black} />}
            className="button-icon mr-1"
            onClick={handleRefresh}
          />
          <ButtonLabelIcon
            id="add-icon"
            text={t('general_button_new')}
            type="button"
            Icon={() => <AddIcon fill={colors.black} />}
            className="button-icon"
            onClick={handleToggleOption}
          />
        </div>
      </div>
      <StatesTable
        loading={loading}
        setCurrentState={setCurrentState}
        setOpenFormModal={setOpenFormModal}
        handleRefresh={handleRefresh}
        urlFragment={urlFragment}
      />
      <Popover trigger="legacy" placement="bottom" target="PopoverFocus">
        <PopoverBody>
          <OptionMenu onClick={() => onSort()} aria-hidden="true">
            <LabelIcon
              label={t('clients_name_column')}
              color=""
              className="ml-1 body-text--primary"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getSortIcon()}
            />
          </OptionMenu>
        </PopoverBody>
      </Popover>
      {showTranslateModal && (
        <TranslateFormModal
          onCloseModal={onTranslate}
          refreshStateTranslateData={refreshStateTranslateData}
          urlFragment={urlFragment}
        />
      )}
      <StateFormModal isOpen={openFormModal} toggle={onCloseFormModal} size="md">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseFormModal}
        />
        <ModalBody>
          <StateForm
            urlFragment={urlFragment}
            onCancel={onCloseFormModal}
            currentState={currentState}
            handleRefresh={handleRefresh}
          />
        </ModalBody>
      </StateFormModal>
    </StatesWrapper>
  );
};

States.propTypes = {
  urlFragment: PropTypes.string.isRequired,
};

export default States;
